import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Plus, Clock, Play, Check, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Badge,
  EmptyState,
  Modal,
  Input,
  Select,
  Dropdown,
  DropdownItem,
  ConfirmDialog,
} from '@/components/ui';
import {
  useCreateTimeEntry,
  useUpdateTimeEntry,
  useDeleteTimeEntry,
  useApproveTimeEntry,
} from '@/hooks/useTimeEntries';
import { usePersonalTimeEntries } from '@/hooks/usePersonalTimeEntries';
import { useProjects, useProjectTasks } from '@/hooks/useProjects';
import { useTimerStore } from '@/stores/timer';
import { useAuthStore } from '@/stores/auth';
import { useForm } from 'react-hook-form';
import { formatDate, formatDuration } from '@aelita/shared';
import type { TimeEntry } from '@aelita/shared';

export const Route = createFileRoute('/time')({
  component: TimeTrackingPage,
});

function SummaryValue({
  label,
  query,
  value,
  hasIdentity,
}: {
  label: string;
  query: { isPending: boolean; isError: boolean };
  value: string | number | undefined;
  hasIdentity: boolean;
}) {
  const context = <span className="sr-only">{label}: </span>;
  if (!hasIdentity) return <span>{context}Unavailable</span>;
  if (query.isError) return <span role="alert">{context}Unavailable</span>;
  if (query.isPending) return <span role="status">{context}Loading…</span>;
  return <>{value ?? 'Unavailable'}</>;
}

export function TimeTrackingPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isStartTimerOpen, setIsStartTimerOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<TimeEntry | null>(null);
  const { list, week, pending, hasIdentity } = usePersonalTimeEntries();
  const { data, isPending: isLoading } = list;
  const deleteEntry = useDeleteTimeEntry();
  const timeEntries = data?.data ?? [];
  const totals = data?.totals;

  const handleDelete = async () => {
    if (!deletingEntry) return;
    try {
      await deleteEntry.mutateAsync(deletingEntry.id);
      setDeletingEntry(null);
    } catch (error) {
      console.error('Failed to delete time entry:', error);
    }
  };

  return (
    <>
      <Header
        title="Time Tracking"
        subtitle="Track and manage your time entries"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              leftIcon={<Play className="h-4 w-4" />}
              onClick={() => setIsStartTimerOpen(true)}
            >
              Start Timer
            </Button>
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setIsCreateOpen(true)}>
              Add Entry
            </Button>
          </div>
        }
      />
      <PageContainer>
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardBody>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Hours</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                <SummaryValue
                  label="Total Hours"
                  query={list}
                  value={totals?.totalHours}
                  hasIdentity={hasIdentity}
                />
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-sm text-gray-500 dark:text-gray-400">Billable Hours</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                <SummaryValue
                  label="Billable Hours"
                  query={list}
                  value={totals?.billableHours}
                  hasIdentity={hasIdentity}
                />
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-sm text-gray-500 dark:text-gray-400">This Week</p>
              <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                <SummaryValue
                  query={week}
                  label="This Week"
                  value={week.data?.totals.totalHours}
                  hasIdentity={hasIdentity}
                />
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending Approval</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                <SummaryValue
                  query={pending}
                  label="Pending Approval"
                  value={pending.data?.pagination.total}
                  hasIdentity={hasIdentity}
                />
              </p>
            </CardBody>
          </Card>
        </div>

        {/* Time Entries Table */}
        <Card>
          {!hasIdentity ? (
            <CardBody>
              <p>Sign in to view your time entries.</p>
            </CardBody>
          ) : list.isError ? (
            <CardBody>
              <p role="alert">Unable to load time entries.</p>
            </CardBody>
          ) : isLoading ? (
            <CardBody>
              <p className="text-gray-500 dark:text-gray-400">Loading time entries...</p>
            </CardBody>
          ) : timeEntries.length === 0 ? (
            <EmptyState
              icon={Clock}
              title="No time entries yet"
              description="Start tracking your time to see entries here"
              action={{
                label: 'Add Entry',
                onClick: () => setIsCreateOpen(true),
              }}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Date</TableHeader>
                  <TableHeader>Project / Task</TableHeader>
                  <TableHeader>Description</TableHeader>
                  <TableHeader>Duration</TableHeader>
                  <TableHeader>Billable</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader className="w-12">{''}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeEntries.map((entry: any) => (
                  <TimeEntryRow
                    key={entry.id}
                    entry={entry}
                    onEdit={setEditingEntry}
                    onDelete={setDeletingEntry}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </PageContainer>

      {/* Create Entry Modal */}
      <TimeEntryFormModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        entry={null}
      />

      {/* Edit Entry Modal */}
      <TimeEntryFormModal
        isOpen={!!editingEntry}
        onClose={() => setEditingEntry(null)}
        entry={editingEntry}
      />

      {/* Start Timer Modal */}
      <StartTimerModal isOpen={isStartTimerOpen} onClose={() => setIsStartTimerOpen(false)} />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingEntry}
        onClose={() => setDeletingEntry(null)}
        onConfirm={handleDelete}
        title="Delete Time Entry"
        message="Are you sure you want to delete this time entry? This action cannot be undone."
        confirmLabel="Delete"
        isLoading={deleteEntry.isPending}
      />
    </>
  );
}

function TimeEntryRow({
  entry,
  onEdit,
  onDelete,
}: {
  entry: any;
  onEdit: (entry: TimeEntry) => void;
  onDelete: (entry: TimeEntry) => void;
}) {
  const approveEntry = useApproveTimeEntry();
  const { user } = useAuthStore();
  const canApprove = user?.role === 'admin' || user?.role === 'manager';

  return (
    <TableRow>
      <TableCell>
        <p className="font-medium">{formatDate(new Date(entry.startTime))}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {new Date(entry.startTime).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
          {entry.endTime &&
            ` - ${new Date(entry.endTime).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}`}
        </p>
      </TableCell>
      <TableCell>
        <button
          onClick={() => onEdit(entry)}
          className="text-left hover:text-primary-600 dark:hover:text-primary-400"
        >
          <p className="font-medium text-gray-900 dark:text-gray-100">{entry.project?.name}</p>
          {entry.task && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{entry.task.title}</p>
          )}
        </button>
      </TableCell>
      <TableCell>
        <p className="text-gray-600 dark:text-gray-300 max-w-xs truncate">
          {entry.description ?? '-'}
        </p>
      </TableCell>
      <TableCell>
        <p className="font-mono font-medium">{formatDuration(entry.duration)}</p>
      </TableCell>
      <TableCell>
        {entry.isBillable ? (
          <Badge variant="green">Billable</Badge>
        ) : (
          <Badge variant="gray">Non-billable</Badge>
        )}
      </TableCell>
      <TableCell>
        {entry.isApproved ? (
          <Badge variant="green">Approved</Badge>
        ) : (
          <Badge variant="yellow">Pending</Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {canApprove && !entry.isApproved && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => approveEntry.mutate(entry.id)}
              isLoading={approveEntry.isPending}
              title="Approve"
            >
              <Check className="h-4 w-4" />
            </Button>
          )}
          <Dropdown
            trigger={
              <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
            }
            align="right"
            width="w-36"
          >
            <DropdownItem onClick={() => onEdit(entry)}>
              <div className="flex items-center gap-2">
                <Pencil className="h-4 w-4" />
                Edit
              </div>
            </DropdownItem>
            <DropdownItem
              onClick={() => onDelete(entry)}
              className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
            >
              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                Delete
              </div>
            </DropdownItem>
          </Dropdown>
        </div>
      </TableCell>
    </TableRow>
  );
}

function TimeEntryFormModal({
  isOpen,
  onClose,
  entry,
}: {
  isOpen: boolean;
  onClose: () => void;
  entry: TimeEntry | null;
}) {
  const { data: projectsData } = useProjects({ status: 'active' });
  const projects = projectsData?.data ?? [];
  const createEntry = useCreateTimeEntry();
  const updateEntry = useUpdateTimeEntry();
  const isEditing = !!entry;

  // Use local state to track selected project for task fetching
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  const { register, handleSubmit, reset, setValue } = useForm();

  // Initialize form and selectedProjectId when modal opens or entry changes
  useEffect(() => {
    if (isOpen) {
      if (entry) {
        // Editing - populate with entry's values
        const entryAny = entry as any;
        setSelectedProjectId(entry.projectId);
        reset({
          projectId: entry.projectId,
          taskId: entryAny.taskId ?? '',
          date: new Date(entry.startTime).toISOString().split('T')[0],
          startTime: new Date(entry.startTime).toTimeString().slice(0, 5),
          endTime: entry.endTime ? new Date(entry.endTime).toTimeString().slice(0, 5) : '17:00',
          description: entry.description ?? '',
          isBillable: entry.isBillable ? 'true' : 'false',
        });
      } else if (projects.length > 0) {
        // Creating - use defaults
        const firstProject = projects[0]!;
        setSelectedProjectId(firstProject.id);
        reset({
          projectId: firstProject.id,
          taskId: '',
          date: new Date().toISOString().split('T')[0],
          startTime: '09:00',
          endTime: '17:00',
          description: '',
          isBillable: 'true',
        });
      }
    }
  }, [isOpen, entry, projects, reset]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedProjectId('');
    }
  }, [isOpen]);

  // Fetch tasks for selected project
  const { data: tasksData } = useProjectTasks(selectedProjectId || undefined);
  const tasks = (tasksData as any[]) ?? [];

  // Handle project change
  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProjectId = e.target.value;
    setSelectedProjectId(newProjectId);
    setValue('taskId', '');
  };

  const handleClose = () => {
    reset();
    setSelectedProjectId('');
    onClose();
  };

  const onSubmit = async (data: any) => {
    try {
      const startTime = new Date(`${data.date}T${data.startTime}`);
      const endTime = new Date(`${data.date}T${data.endTime}`);

      if (isEditing) {
        await updateEntry.mutateAsync({
          id: entry.id,
          data: {
            projectId: data.projectId,
            taskId: data.taskId || null,
            description: data.description,
            startTime,
            endTime,
            isBillable: data.isBillable === 'true',
          },
        });
      } else {
        await createEntry.mutateAsync({
          projectId: data.projectId,
          taskId: data.taskId || null,
          description: data.description,
          startTime,
          endTime,
          isBillable: data.isBillable === 'true',
        });
      }
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to save time entry:', error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Time Entry' : 'Add Time Entry'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Select
          label="Project"
          {...register('projectId', { required: true })}
          onChange={handleProjectChange}
          options={projects.map((p: any) => ({ value: p.id, label: p.name }))}
        />

        <Select
          label="Task (optional)"
          {...register('taskId')}
          options={[
            { value: '', label: 'No task' },
            ...tasks
              .filter((t: any) => {
                // Show active tasks, or the currently selected task even if done
                const entryTaskId = (entry as any)?.taskId;
                return (t.status !== 'done' && t.status !== 'cancelled') || t.id === entryTaskId;
              })
              .map((t: any) => ({ value: t.id, label: t.title })),
          ]}
        />

        <Input label="Date" type="date" {...register('date', { required: true })} />

        <div className="grid grid-cols-2 gap-4">
          <Input label="Start Time" type="time" {...register('startTime', { required: true })} />
          <Input label="End Time" type="time" {...register('endTime', { required: true })} />
        </div>

        <div>
          <label className="label">Description</label>
          <textarea
            {...register('description')}
            className="input min-h-[60px]"
            placeholder="What did you work on?"
          />
        </div>

        <Select
          label="Billable"
          {...register('isBillable')}
          options={[
            { value: 'true', label: 'Yes - Billable' },
            { value: 'false', label: 'No - Non-billable' },
          ]}
        />

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={createEntry.isPending || updateEntry.isPending}>
            {isEditing ? 'Save Changes' : 'Add Entry'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function StartTimerModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { data: projectsData } = useProjects({ status: 'active' });
  const projects = projectsData?.data ?? [];
  const { startTimer, isLoading } = useTimerStore();

  // Use local state to track selected project for task fetching
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  const { register, handleSubmit, reset, setValue } = useForm();

  // Initialize selectedProjectId when projects load and modal opens
  useEffect(() => {
    if (isOpen && projects.length > 0 && !selectedProjectId) {
      // Set to first project since that's what the select will show by default
      setSelectedProjectId(projects[0]!.id);
    }
  }, [isOpen, projects, selectedProjectId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedProjectId('');
    }
  }, [isOpen]);

  // Fetch tasks for selected project
  const { data: tasksData } = useProjectTasks(selectedProjectId || undefined);
  const tasks = (tasksData as any[]) ?? [];

  // Handle project change
  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProjectId = e.target.value;
    setSelectedProjectId(newProjectId);
    setValue('taskId', '');
  };

  const handleClose = () => {
    reset();
    setSelectedProjectId('');
    onClose();
  };

  const onSubmit = async (data: any) => {
    try {
      await startTimer(data.projectId, data.taskId || null, data.description || null);
      reset();
      setSelectedProjectId('');
      onClose();
    } catch (error) {
      console.error('Failed to start timer:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Start Timer">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Select
          label="Project"
          {...register('projectId', { required: true })}
          onChange={handleProjectChange}
          options={projects.map((p: any) => ({ value: p.id, label: p.name }))}
        />

        <Select
          label="Task (optional)"
          {...register('taskId')}
          options={[
            { value: '', label: 'No task' },
            ...tasks
              .filter((t: any) => t.status !== 'done' && t.status !== 'cancelled')
              .map((t: any) => ({ value: t.id, label: t.title })),
          ]}
        />

        <div>
          <label className="label">Description (optional)</label>
          <textarea
            {...register('description')}
            className="input min-h-[60px]"
            placeholder="What are you working on?"
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading} leftIcon={<Play className="h-4 w-4" />}>
            Start Timer
          </Button>
        </div>
      </form>
    </Modal>
  );
}
